
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, Eye, EyeOff } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

interface AdminProductsListProps {
  onEdit: (productId: string) => void;
}

interface ProductWithAggregates {
  id: string;
  name: string;
  description: string;
  price: number;
  published: boolean;
  variant_count: number;
  product_categories: Array<{ categories: { name: string } }>;
}

const AdminProductsList = ({ onEdit }: AdminProductsListProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  console.log("AdminProductsList: Rendering products list");

  const { data: products, isLoading } = useQuery({
    queryKey: ['admin-products'],
    queryFn: async () => {
      console.log("AdminProductsList: Fetching products data");
      
      const { data: rawProducts, error } = await supabase
        .from('products')
        .select(`
          *,
          product_variants (id),
          product_categories (
            categories (
              name
            )
          )
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error("AdminProductsList: Error fetching products:", error);
        throw error;
      }

      return rawProducts?.map((product: any) => ({
        id: product.id,
        name: product.name,
        description: product.description,
        price: product.price,
        published: product.published,
        variant_count: product.product_variants?.length || 0,
        product_categories: product.product_categories?.map((pc: any) => ({
          categories: { name: pc.categories?.name }
        })) || []
      })) as ProductWithAggregates[];
    },
    staleTime: 1000 * 30, // Cache pendant 30 secondes
    refetchOnWindowFocus: false
  });

  const togglePublished = async (productId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('products')
        .update({ published: !currentStatus })
        .eq('id', productId);

      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ['admin-products'] });
      await queryClient.invalidateQueries({ queryKey: ['products'] });

      toast({
        title: currentStatus ? "Produit dépublié" : "Produit publié",
        description: `Le produit a été ${currentStatus ? "dépublié" : "publié"} avec succès`,
      });
    } catch (error) {
      console.error("Error toggling product status:", error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la modification du statut",
        variant: "destructive",
      });
    }
  };

  const deleteProduct = async (productId: string) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce produit ?")) {
      return;
    }

    try {
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', productId);

      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ['admin-products'] });
      await queryClient.invalidateQueries({ queryKey: ['products'] });

      toast({
        title: "Produit supprimé",
        description: "Le produit a été supprimé avec succès",
      });
    } catch (error) {
      console.error("Error deleting product:", error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la suppression",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Nom
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Prix
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Catégorie(s)
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Variantes
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Statut
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {products?.map((product) => (
              <tr key={product.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {product.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {product.price.toFixed(2)} $
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 max-w-[200px]">
                  {product.product_categories && product.product_categories.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {product.product_categories.map((category, index) => (
                        <span 
                          key={`${product.id}-cat-${index}`} 
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {category.categories?.name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-500">N/A</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {product.variant_count} variante(s)
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    product.published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {product.published ? 'Publié' : 'Non publié'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => togglePublished(product.id, product.published)}
                    >
                      {product.published ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(product.id)}
                      title="Modifier le produit"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => deleteProduct(product.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminProductsList;
